import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import { peopleSearchResponseSchema, type PeopleSearch } from './types';

/** Personas del QMS (empresa activa) y del LMS que coinciden con `q` (≥ 2 caracteres).
 *  `''` lista las personas del QMS (QA F1 COD-07); `null` no consulta. */
export function usePeopleSearch(q: string | null): UseQueryResult<PeopleSearch> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['people-search', activeCompanyId, q],
    queryFn: async () => {
      const raw = await api.get<unknown>(`/api/v1/admin/people?q=${encodeURIComponent(q ?? '')}`);
      return peopleSearchResponseSchema.parse(raw).data;
    },
    enabled: q !== null && (q === '' || q.length >= 2) && Boolean(activeCompanyId),
    staleTime: 30_000,
  });
}

/** Da de alta en el QMS a una persona del LMS antes de su primer ingreso. Devuelve su id. */
export function useAddFromLms(): ReturnType<
  typeof useMutation<string, Error, { authSubject: string }>
> {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ authSubject }) => {
      const res = await api.post<{ data: { userId: string } }>('/api/v1/admin/people/from-lms', {
        authSubject,
      });
      return res.data.userId;
    },
  });
}
